import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api } from "@/client/lib/api";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import { Checkbox } from "@/client/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectPopup, SelectItem } from "@/client/components/ui/select";
import {
  UsersIcon,
  DownloadIcon,
  CopyIcon,
  CheckIcon,
  MailIcon,
  PhoneIcon,
  AlertTriangleIcon,
  HistoryIcon,
  MapPinIcon,
  CalendarClockIcon,
  PlusIcon,
  MinusIcon,
  PencilIcon,
  XCircleIcon,
} from "lucide-react";
import { offeringTitle as resolveOfferingTitle } from "@/shared/offering-title";

// ─── snapshot shape ───
// The admin version endpoints spread a full submission snapshot at the top level. Both flows write
// the SAME full snapshot, so every version carries all of contacts/groups/constraints/programmes;
// `flow` tells us which flow's "Indienen" froze it (so we lead with that flow's data).

type Pattern = { dayOfWeekCode: string; startTime: string | null; endTime: string | null };
type Constraint = {
  id: number;
  typeCode: string;
  title: string;
  appliesToEntireSchool: boolean;
  specificDates: Array<{ date: string; startTime: string | null; endTime: string | null }>;
  dateRanges: Array<{ startDate: string; endDate: string }>;
  weeklyPatterns: Pattern[];
  groupIds: number[];
};
type VoorkeurRange = { startDate: string; endDate: string; startTime: string | null; endTime: string | null };
type PerGroupEntry = {
  voorkeur?: { geenVoorkeur?: boolean; ranges?: VoorkeurRange[] };
  addonAnswers?: Array<{ addonCode: string; wanted: boolean }>;
};
type Selection = {
  id: number;
  offeringId: number;
  statusCode: string;
  originCode: string;
  notes: string | null;
  perGroup?: Record<string, PerGroupEntry>;
};
type Submission = {
  hasSubmission?: boolean;
  deadline?: string | null;
  versionNo?: number;
  totalVersions?: number;
  submittedAt?: string;
  submittedBy?: string | null;
  flow?: string | null;
  postDeadline?: boolean;
  schoolName: string;
  schoolInfo?: {
    wijkCode: string | null;
    phoneNumber: string | null;
    visitAddress: {
      streetName: string;
      houseNumber: string;
      houseNumberSuffix: string | null;
      postalCode: string;
      city: string;
    } | null;
  } | null;
  emergencyContact?: { givenName: string | null; familyName: string | null; phoneNumber: string | null } | null;
  contacts: Array<{ role: string; name: string; emails: string[]; phones: string[] }>;
  groups: Array<{ displayName: string; yearLevels: Array<{ code: string; studentCount: number }> }>;
  constraints: Constraint[];
  selections: Selection[];
  selectionGroups: Array<{ selectionId: number; classGroupId: number; includeFullGroup: boolean; requestedStudentCount: number | null }>;
  selectionGroupYearLevels?: Array<{ selectionId: number; classGroupId: number; yearLevelCode: string }>;
  // Per-group "doet dit jaar niet mee" opt-outs (keuze / technohub) + school-wide section opt-outs
  // (deskundigheidsbevordering / maatwerk / technohub). Captured in the snapshot; surfaced so the
  // admin programme overview mirrors the school's Controleren page (incl. the "niet mee"-regels).
  optOuts?: Array<{ classGroupId: number; enrolmentModeCode: string }>;
  sectionOptOuts?: string[];
  offerings: Array<{ id: number; programmeId: number; providerOrganizationId: number; titleOverride: string | null; enrolmentModeCode: string }>;
  programmes: Array<{ id: number; title: string }>;
  providers: Array<{ id: number; legalName: string; displayName: string | null }>;
  groupNames: Record<string, string>;
  groupDetails?: Record<string, { displayName: string; yearLevels: Array<{ code: string; studentCount: number | null }> }>;
  offeringAddons?: Record<string, Array<{ addonCode: string; label: string }>>;
  offeringHasDateBlocks?: Record<string, boolean>;
};

const ROLE_LABELS: Record<string, string> = {
  directeur: "Directeur", cultuurcoordinator: "Kunstcoördinator", kunstcoordinator: "Kunstcoördinator",
  leerkracht: "Leerkracht", ib_er: "Intern begeleider", administratie: "Administratie",
};
const CONSTRAINT_LABELS: Record<string, string> = {
  closure: "Studiedag", recurring_unavailable: "Vrij dagdeel", vakdocent: "Vakdocent", avoidance: "Vermijdperiode", resource_unavailable: "Resource",
};
const MODE_LABELS: Record<string, string> = {
  vast: "Vast programma", keuze: "Keuzeprogramma", technohub: "TechnoHub", deskundigheidsbevordering: "Deskundigheidsbevordering", maatwerk: "Maatwerk",
};
const STATUS_BADGE: Record<string, { label: string; variant: "success" | "warning" | "outline" | "error" }> = {
  confirmed: { label: "Bevestigd", variant: "success" },
  enrolled: { label: "Toegewezen", variant: "warning" },
  interested: { label: "Gekozen", variant: "outline" },
  waitlisted: { label: "Wachtlijst", variant: "outline" },
  pending: { label: "In behandeling", variant: "outline" },
  opted_out: { label: "Afgemeld", variant: "error" },
};
const WEEKDAY_LABELS: Record<string, string> = {
  monday: "Maandag", tuesday: "Dinsdag", wednesday: "Woensdag", thursday: "Donderdag", friday: "Vrijdag",
};
const DUTCH_MONTHS = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];
const yl = (code: string) => `Leerjaar ${code.replace("groep_", "")}`;
const isPlaceholder = (t: string) => t.startsWith("__");

function dayPart(startTime: string | null, endTime: string | null): string {
  if (!startTime && !endTime) return "hele dag";
  const s = startTime ?? "";
  if (s.startsWith("08") || (s && s < "12:00")) return "ochtend";
  return "middag";
}
function fmtDate(iso: string): string {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}
const fmtDay = (code: string) => WEEKDAY_LABELS[code] ?? code;
function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
/** "2026-09-01" -> "September 2026" (capitalized Dutch month + year). Mirrors the admin read-model. */
function periodLabel(startDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(startDate));
  if (!m) return String(startDate);
  const month = DUTCH_MONTHS[Number(m[2]) - 1] ?? m[2]!;
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${m[1]}`;
}

/** Dates / date-range text for closure + avoidance constraints. */
function datesText(c: Constraint): string {
  if (c.specificDates.length) return c.specificDates.map((d) => fmtDate(d.date)).join(", ");
  if (c.dateRanges.length) return c.dateRanges.map((r) => `${fmtDate(r.startDate)} t/m ${fmtDate(r.endDate)}`).join(", ");
  return "";
}
/** Day + daypart text for recurring / vakdocent constraints. */
function momentsText(c: Constraint): string {
  return c.weeklyPatterns.map((w) => `${fmtDay(w.dayOfWeekCode)} ${dayPart(w.startTime, w.endTime)}`).join(", ");
}
function constraintDetail(c: Constraint): string {
  if (c.typeCode === "recurring_unavailable" || c.typeCode === "vakdocent") return momentsText(c);
  return datesText(c);
}

// ─── per-(selection,group) voorkeur + add-on, identical wording to the server read-model ───

/** Voorkeur text for one (selection, group): all four states, exactly as the activity table. */
function voorkeurText(entry: PerGroupEntry | undefined, offeringHasDateBlocks: boolean): string {
  if (!offeringHasDateBlocks) return "Niet van toepassing";
  const ranges = entry?.voorkeur?.ranges ?? [];
  if (ranges.length) {
    return ranges.map((r) => periodLabel(r.startDate)).filter((x, i, a) => a.indexOf(x) === i).join(", ");
  }
  if (entry?.voorkeur?.geenVoorkeur) return "Geen voorkeur";
  return "Niet ingevuld";
}
/** Add-on answers for one (selection, group): "Label: Ja/Nee/Niet beantwoord", or "Geen add-ons". */
function addonText(offeringAddons: Array<{ addonCode: string; label: string }>, answers: PerGroupEntry["addonAnswers"]): string {
  if (!offeringAddons.length) return "Geen add-ons";
  const byCode = new Map((answers ?? []).map((a) => [a.addonCode, a.wanted]));
  return offeringAddons
    .map((ad) => {
      const w = byCode.get(ad.addonCode);
      return `${ad.label}: ${w === undefined ? "Niet beantwoord" : w ? "Ja" : "Nee"}`;
    })
    .join(", ");
}

function Section({ title, count, action, className, children }: { title: string; count?: number; action?: React.ReactNode; className?: string; children: React.ReactNode }) {
  return (
    <section className={`rounded-lg border ${className ?? ""}`}>
      <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-4 py-2.5">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          {title}
          {count != null && <span className="rounded-full bg-muted px-1.5 text-xs font-normal text-muted-foreground">{count}</span>}
        </h2>
        {action}
      </div>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      aria-label={`Kopieer ${text}`}
      title="Kopiëren"
    >
      {copied ? <CheckIcon size={13} className="text-emerald-600" /> : <CopyIcon size={13} />}
    </button>
  );
}

// ─── helper: resolve offering title / provider / mode from a snapshot ───
function offeringHelpers(d: Submission) {
  const offering = (id: number) => d.offerings.find((o) => o.id === id);
  const title = (id: number) => {
    const o = offering(id);
    const p = o ? d.programmes.find((x) => x.id === o.programmeId) : null;
    return resolveOfferingTitle(o?.titleOverride, p?.title, id);
  };
  const provider = (id: number) => {
    const o = offering(id);
    const pr = o ? d.providers.find((x) => x.id === o.providerOrganizationId) : null;
    return pr ? (pr.displayName ?? pr.legalName) : "";
  };
  const mode = (id: number) => offering(id)?.enrolmentModeCode ?? "";
  return { offering, title, provider, mode };
}

// ─── CSV export ───
function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? "" : String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function buildCsv(data: Submission): string {
  const { offering, title, provider } = offeringHelpers(data);
  const rows: (string | number | null)[][] = [];
  rows.push(["Sectie", "Type", "Naam", "Detail", "Groepen / extra"]);

  for (const ct of data.contacts) {
    rows.push(["Contactpersoon", ROLE_LABELS[ct.role] ?? ct.role, ct.name, [...ct.emails, ...ct.phones].join(" / "), ""]);
  }
  for (const g of data.groups) {
    const total = g.yearLevels.reduce((s, y) => s + (y.studentCount ?? 0), 0);
    rows.push(["Groep", g.yearLevels.map((y) => `${yl(y.code)} (${y.studentCount})`).join(", "), g.displayName, `${total} leerlingen`, ""]);
  }
  for (const c of data.constraints.filter((x) => !isPlaceholder(x.title))) {
    const groups = c.appliesToEntireSchool ? "Hele school" : c.groupIds.map((id) => data.groupNames[id] ?? `Groep ${id}`).join(", ");
    rows.push(["Beschikbaarheid", CONSTRAINT_LABELS[c.typeCode] ?? c.typeCode, c.title, constraintDetail(c), groups]);
  }
  for (const s of data.selections.filter((x) => x.statusCode !== "cancelled")) {
    const o = offering(s.offeringId);
    const grps = data.selectionGroups.filter((g) => g.selectionId === s.id)
      .map((g) => `${data.groupNames[g.classGroupId] ?? `Groep ${g.classGroupId}`}${!g.includeFullGroup && g.requestedStudentCount != null ? ` (${g.requestedStudentCount} ll.)` : ""}`)
      .join(", ");
    rows.push([MODE_LABELS[o?.enrolmentModeCode ?? ""] ?? "Programma", provider(s.offeringId), title(s.offeringId), s.notes ?? "", grps]);
  }
  return rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
}
function downloadCsv(data: Submission, csv: string) {
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${data.schoolName.replace(/[^\w\d-]+/g, "_")}_aanlevering.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type VersionMeta = { versionNo: number; submittedAt: string; flow: string | null; submittedBy: string | null; postDeadline: boolean };
const FLOW_LABEL: Record<string, string> = { schoolgegevens: "Schoolgegevens", programmaselectie: "Programmaselectie" };
const flowLabel = (flow: string | null | undefined) => (flow ? FLOW_LABEL[flow] ?? flow : "Aanlevering");

// ═══════════════════════════ DIFF ═══════════════════════════
// We compare the selected version to the PREVIOUS version of the SAME flow, per section. Each section
// is reduced to a flat, human-readable list of "Label = waarde" lines; we then bucket lines into
// added / removed / changed. A "changed" pair is two lines that share the SAME label but a different
// value (so KUVO sees the old vs new value, not a remove+add). Everything stays human copy — no JSON.

type DiffLine = { label: string; value: string };
type SectionDiff = {
  section: string;
  added: DiffLine[];
  removed: DiffLine[];
  changed: Array<{ label: string; from: string; to: string }>;
};

/** Build the labeled lines per section for one snapshot, for the flow being viewed. */
function diffLines(d: Submission, flow: string | null | undefined): Record<string, DiffLine[]> {
  const { title, provider, mode } = offeringHelpers(d);
  const out: Record<string, DiffLine[]> = {};

  if (flow !== "programmaselectie") {
    // Schoolgegevens-owned sections.
    if (d.schoolInfo?.visitAddress) {
      const a = d.schoolInfo.visitAddress;
      out["Bezoekadres"] = [{ label: "Adres", value: `${a.streetName} ${a.houseNumber}${a.houseNumberSuffix ?? ""}, ${a.postalCode} ${a.city}` }];
    }
    out["Contactpersonen"] = d.contacts.map((c) => ({ label: c.name, value: `${ROLE_LABELS[c.role] ?? c.role} · ${[...c.emails, ...c.phones].join(", ") || "geen gegevens"}` }));
    out["Groepen en leerlingen"] = d.groups.map((g) => ({
      label: g.displayName,
      value: `${g.yearLevels.reduce((s, y) => s + (y.studentCount ?? 0), 0)} leerlingen · ${g.yearLevels.map((y) => `${yl(y.code)} (${y.studentCount})`).join(", ")}`,
    }));
    const real = d.constraints.filter((c) => !isPlaceholder(c.title));
    out["Beschikbaarheid"] = real.map((c) => ({ label: `${CONSTRAINT_LABELS[c.typeCode] ?? c.typeCode}: ${c.title}`, value: constraintDetail(c) || "—" }));
  } else {
    // Programmaselectie-owned, mirroring the Controleren categories so the diff covers every
    // section (incl. the school-wide deskundigheidsbevordering / maatwerk and the "niet mee"-regels).
    const liveSels = d.selections.filter((s) => s.statusCode !== "cancelled");
    const optOuts = d.optOuts ?? [];
    const sectionOptOuts = d.sectionOptOuts ?? [];
    const gName = (gid: number) => d.groupNames[gid] ?? d.groupDetails?.[String(gid)]?.displayName ?? `Groep ${gid}`;
    const selLines = (m: string): DiffLine[] => {
      const lines: DiffLine[] = [];
      for (const s of liveSels.filter((x) => mode(x.offeringId) === m)) {
        const grps = d.selectionGroups.filter((g) => g.selectionId === s.id);
        const hasBlocks = d.offeringHasDateBlocks?.[String(s.offeringId)] ?? false;
        const addons = d.offeringAddons?.[String(s.offeringId)] ?? [];
        if (grps.length === 0) {
          // School-wide selection (deskundigheidsbevordering / maatwerk): no group binding.
          const parts = [provider(s.offeringId) || "onbekende aanbieder"];
          if (s.notes) parts.push(`“${s.notes}”`);
          lines.push({ label: title(s.offeringId), value: parts.join(" · ") });
          continue;
        }
        for (const g of grps) {
          const entry = s.perGroup?.[String(g.classGroupId)];
          const parts = [`Voorkeur: ${voorkeurText(entry, hasBlocks)}`];
          const ad = addonText(addons, entry?.addonAnswers);
          if (ad !== "Geen add-ons") parts.push(ad);
          lines.push({ label: `${title(s.offeringId)} — ${gName(g.classGroupId)}`, value: parts.join(" · ") });
        }
      }
      return lines;
    };
    const optOutLines = (m: string): DiffLine[] =>
      optOuts.filter((o) => o.enrolmentModeCode === m).map((o) => ({ label: `Doet niet mee — ${gName(o.classGroupId)}`, value: "afgemeld" }));
    const sectionNiet = (m: string, label: string): DiffLine[] =>
      sectionOptOuts.includes(m) ? [{ label, value: "doet dit jaar niet mee" }] : [];

    out["Vast- & keuzeprogramma"] = [...selLines("vast"), ...selLines("keuze"), ...optOutLines("keuze")];
    out["TechnoHub"] = [...selLines("technohub"), ...optOutLines("technohub"), ...sectionNiet("technohub", "TechnoHub")];
    out["Deskundigheidsbevordering"] = sectionOptOuts.includes("deskundigheidsbevordering")
      ? sectionNiet("deskundigheidsbevordering", "Deskundigheidsbevordering")
      : selLines("deskundigheidsbevordering");
    out["Extra maatwerk"] = sectionOptOuts.includes("maatwerk")
      ? sectionNiet("maatwerk", "Extra maatwerk")
      : selLines("maatwerk");
  }
  return out;
}

function diffSection(base: DiffLine[], target: DiffLine[]): Omit<SectionDiff, "section"> {
  const baseByLabel = new Map(base.map((l) => [l.label, l]));
  const targetByLabel = new Map(target.map((l) => [l.label, l]));
  const added: DiffLine[] = [];
  const removed: DiffLine[] = [];
  const changed: Array<{ label: string; from: string; to: string }> = [];
  for (const t of target) {
    const b = baseByLabel.get(t.label);
    if (!b) added.push(t);
    else if (b.value !== t.value) changed.push({ label: t.label, from: b.value, to: t.value });
  }
  for (const b of base) {
    if (!targetByLabel.has(b.label)) removed.push(b);
  }
  return { added, removed, changed };
}

/** Readable "what changed" panel: green added, red removed, amber changed, per section. */
function VersionDiff({ base, target, baseNo, targetNo, flow }: { base: Submission; target: Submission; baseNo: number; targetNo: number; flow: string | null | undefined }) {
  const nb = diffLines(base, flow);
  const nt = diffLines(target, flow);
  const sections = Array.from(new Set([...Object.keys(nb), ...Object.keys(nt)]));
  const diffs: SectionDiff[] = sections
    .map((s) => ({ section: s, ...diffSection(nb[s] ?? [], nt[s] ?? []) }))
    .filter((d) => d.added.length || d.removed.length || d.changed.length);

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <HistoryIcon size={15} /> Wijzigingen in versie {targetNo} ten opzichte van versie {baseNo}
      </h3>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Vergelijking met de vorige aanlevering van dezelfde soort ({flowLabel(flow)}).
      </p>

      {diffs.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">Geen inhoudelijke verschillen tussen deze twee versies.</p>
      ) : (
        <div className="mt-3 space-y-4">
          {diffs.map((d) => (
            <div key={d.section}>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{d.section}</p>
              <ul className="mt-1.5 space-y-1.5 text-sm">
                {d.changed.map((c) => (
                  <li key={"~" + c.label} className="rounded-md border border-amber-300/70 bg-amber-50 px-2.5 py-1.5">
                    <span className="flex items-center gap-1.5 font-medium text-amber-800">
                      <PencilIcon size={12} className="shrink-0" /> {c.label}
                    </span>
                    <span className="mt-0.5 block text-amber-900/90">
                      <span className="text-muted-foreground line-through">{c.from}</span>
                      <span className="mx-1.5 text-amber-700" aria-hidden="true">→</span>
                      <span className="font-medium">{c.to}</span>
                    </span>
                  </li>
                ))}
                {d.added.map((a) => (
                  <li key={"+" + a.label} className="flex items-start gap-1.5 rounded-md border border-emerald-300/70 bg-emerald-50 px-2.5 py-1.5 text-emerald-900">
                    <PlusIcon size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span><span className="font-medium">{a.label}</span>{a.value ? <span className="text-emerald-800/80"> — {a.value}</span> : null}</span>
                  </li>
                ))}
                {d.removed.map((r) => (
                  <li key={"-" + r.label} className="flex items-start gap-1.5 rounded-md border border-red-300/70 bg-red-50 px-2.5 py-1.5 text-red-900">
                    <MinusIcon size={13} className="mt-0.5 shrink-0 text-red-600" />
                    <span><span className="font-medium line-through">{r.label}</span>{r.value ? <span className="text-red-800/80"> — {r.value}</span> : null}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════ SCHOOLGEGEVENS RENDER ═══════════════════════════

function SchoolgegevensView({ data }: { data: Submission }) {
  const groupLabel = (id: number) => data.groupNames[id] ?? `Groep ${id}`;
  const scopeGroups = (c: Constraint): string | null => {
    if (c.appliesToEntireSchool) return "Hele school";
    if (!c.groupIds.length) return null;
    return c.groupIds.map(groupLabel).join(", ");
  };
  const real = data.constraints.filter((c) => !isPlaceholder(c.title));
  const closures = real.filter((c) => c.typeCode === "closure" || c.typeCode === "avoidance");
  const dagdelen = real.filter((c) => c.typeCode === "recurring_unavailable");
  const vakdocenten = real.filter((c) => c.typeCode === "vakdocent");
  const addr = data.schoolInfo?.visitAddress;
  const nood = data.emergencyContact;
  const noodName = nood ? [nood.givenName, nood.familyName].filter(Boolean).join(" ") : "";

  const datedEntry = (c: Constraint) => (
    <li key={c.id} className="py-2 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-medium text-foreground">{c.title}</span>
        {c.typeCode === "avoidance" && <Badge variant="outline" size="sm">vermijden</Badge>}
      </div>
      {datesText(c) && <p className="text-sm text-muted-foreground">{datesText(c)}</p>}
    </li>
  );
  const momentEntry = (c: Constraint) => {
    const grp = scopeGroups(c);
    return (
      <li key={c.id} className="py-2 first:pt-0 last:pb-0">
        <span className="text-sm font-medium text-foreground">{c.title}</span>
        {momentsText(c) && <p className="text-sm text-muted-foreground">{momentsText(c)}</p>}
        {grp && (
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <UsersIcon size={12} /> {grp}
          </p>
        )}
      </li>
    );
  };

  return (
    <div className="gap-4 lg:columns-2 [&>section]:mb-4 [&>section]:break-inside-avoid">
      {(addr || data.schoolInfo?.phoneNumber || noodName) && (
        <Section title="Schoolgegevens">
          <div className="space-y-3 text-sm">
            {addr && (
              <div className="flex items-start gap-2">
                <MapPinIcon size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Bezoekadres</p>
                  <p className="text-foreground">{addr.streetName} {addr.houseNumber}{addr.houseNumberSuffix ?? ""}</p>
                  <p className="text-foreground">{addr.postalCode} {addr.city}</p>
                </div>
              </div>
            )}
            {data.schoolInfo?.phoneNumber && (
              <div className="flex items-center gap-2">
                <PhoneIcon size={14} className="shrink-0 text-muted-foreground" />
                <span className="text-foreground">{data.schoolInfo.phoneNumber}</span>
                <CopyButton text={data.schoolInfo.phoneNumber} />
              </div>
            )}
            {noodName && (
              <div className="flex items-start gap-2 border-t pt-3">
                <AlertTriangleIcon size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Noodcontactpersoon</p>
                  <p className="text-foreground">{noodName}{nood?.phoneNumber ? ` · ${nood.phoneNumber}` : ""}</p>
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="Contactpersonen" count={data.contacts.length}>
        {data.contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Geen contactpersonen.</p>
        ) : (
          <div className="divide-y divide-border">
            {data.contacts.map((ct, i) => (
              <div key={i} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">{ct.name}</span>
                  <Badge variant="outline" size="sm">{ROLE_LABELS[ct.role] ?? ct.role}</Badge>
                </div>
                <div className="mt-1 space-y-1">
                  {ct.emails.map((e) => (
                    <div key={e} className="flex items-center gap-1.5">
                      <MailIcon size={13} className="shrink-0 text-muted-foreground" />
                      <a href={`mailto:${e}`} className="truncate text-sm text-foreground hover:underline">{e}</a>
                      <CopyButton text={e} />
                    </div>
                  ))}
                  {ct.phones.map((p) => (
                    <div key={p} className="flex items-center gap-1.5">
                      <PhoneIcon size={13} className="shrink-0 text-muted-foreground" />
                      <span className="text-sm text-foreground">{p}</span>
                      <CopyButton text={p} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Groepen en leerlingen" count={data.groups.length}>
        {data.groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">Geen groepen.</p>
        ) : (
          <div className="divide-y divide-border">
            {data.groups.map((g, i) => {
              const total = g.yearLevels.reduce((s, y) => s + (y.studentCount ?? 0), 0);
              const combi = g.yearLevels.length > 1;
              return (
                <div key={i} className="py-2 first:pt-0 last:pb-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-foreground">{g.displayName}</span>
                    <span className="shrink-0 text-sm tabular-nums text-muted-foreground">{total} ll.</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    {g.yearLevels.map((y) => (
                      <span key={y.code}>{yl(y.code)}{combi && <span className="tabular-nums"> · {y.studentCount}</span>}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section title="Studiedagen" count={closures.length}>
        {closures.length === 0 ? (
          <p className="text-sm text-muted-foreground">Geen studiedagen of vermijdperiodes opgegeven.</p>
        ) : (
          <ul className="divide-y divide-border">{closures.map(datedEntry)}</ul>
        )}
      </Section>

      <Section title="Vrije dagdelen" count={dagdelen.length}>
        {dagdelen.length === 0 ? (
          <p className="text-sm text-muted-foreground">Geen vrije dagdelen opgegeven.</p>
        ) : (
          <ul className="divide-y divide-border">{dagdelen.map(momentEntry)}</ul>
        )}
      </Section>

      <Section title="Vakdocenten" count={vakdocenten.length}>
        {vakdocenten.length === 0 ? (
          <p className="text-sm text-muted-foreground">Geen vakdocenten opgegeven.</p>
        ) : (
          <ul className="divide-y divide-border">{vakdocenten.map(momentEntry)}</ul>
        )}
      </Section>
    </div>
  );
}

// ═══════════════════════════ PROGRAMMASELECTIE RENDER ═══════════════════════════
// Mirrors the school's own Controleren page: programmekeuzes grouped by CATEGORY (Vast- &
// keuzeprogramma / TechnoHub / Deskundigheidsbevordering / Extra maatwerk). Each category lists its
// activiteiten with — per deelnemende groep — de leerjaren + leerlingaantallen, de voorkeursperiode
// en de add-on antwoorden, plus de "doet dit jaar niet mee"-regels. This surfaces the school-wide
// secties (deskundigheidsbevordering, maatwerk) and the opt-outs that the old per-group layout
// dropped, so the admin sees exactly what the school submitted.

function ProgrammaselectieView({ data }: { data: Submission }) {
  const { title, provider, mode } = offeringHelpers(data);
  const optOuts = data.optOuts ?? [];
  const sectionOptOuts = data.sectionOptOuts ?? [];
  const live = data.selections.filter((s) => s.statusCode !== "cancelled");
  const ylNum = (c: string) => Number(c.replace("groep_", "")) || 99;
  const groupName = (gid: number) =>
    data.groupDetails?.[String(gid)]?.displayName ?? data.groupNames[gid] ?? `Groep ${gid}`;
  const byMode = (m: string) => live.filter((s) => mode(s.offeringId) === m);
  const optedOutGroups = (m: string) =>
    optOuts
      .filter((o) => o.enrolmentModeCode === m)
      .map((o) => groupName(o.classGroupId))
      .sort((a, b) => a.localeCompare(b, "nl", { numeric: true }));

  // One activity: title + provider + status, then a per-group block (leerjaren/leerlingen,
  // voorkeur, add-ons). School-wide selections (deskundigheidsbevordering / maatwerk) have no group
  // binding, so they render as just the activity + any vrije toelichting.
  function renderSelection(s: Selection) {
    const grps = data.selectionGroups
      .filter((g) => g.selectionId === s.id)
      .sort((a, b) => groupName(a.classGroupId).localeCompare(groupName(b.classGroupId), "nl", { numeric: true }));
    const hasBlocks = data.offeringHasDateBlocks?.[String(s.offeringId)] ?? false;
    const addons = data.offeringAddons?.[String(s.offeringId)] ?? [];
    const st = STATUS_BADGE[s.statusCode];
    return (
      <div key={s.id} className="px-4 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium text-foreground">{title(s.offeringId)}</span>
            {provider(s.offeringId) && <span className="text-xs text-muted-foreground">{provider(s.offeringId)}</span>}
          </div>
          {st && <Badge variant={st.variant} size="sm">{st.label}</Badge>}
        </div>
        {grps.length > 0 && (
          <div className="mt-2 space-y-2">
            {grps.map((g) => {
              const gid = g.classGroupId;
              const entry = s.perGroup?.[String(gid)];
              const gd = data.groupDetails?.[String(gid)];
              const countByCode = new Map((gd?.yearLevels ?? []).map((y) => [y.code, y.studentCount]));
              const participating = (data.selectionGroupYearLevels ?? []).filter(
                (p) => p.selectionId === s.id && p.classGroupId === gid,
              );
              const codes = (participating.length
                ? participating.map((p) => p.yearLevelCode)
                : (gd?.yearLevels ?? []).map((y) => y.code)
              ).sort((a, b) => ylNum(a) - ylNum(b));
              const leerjaren = codes.map((c) => ylNum(c)).join(", ");
              const leerlingen = codes.map((c) => { const v = countByCode.get(c); return v == null ? "?" : String(v); }).join(", ");
              const voorkeur = voorkeurText(entry, hasBlocks);
              const addon = addonText(addons, entry?.addonAnswers);
              return (
                <div key={gid} className="border-l-2 border-border pl-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                      <UsersIcon size={13} className="text-muted-foreground" />
                      {groupName(gid)}
                    </span>
                    {codes.length > 0 && (
                      <span className="text-xs text-muted-foreground tabular-nums">
                        leerjaar {leerjaren} · {leerlingen} ll.
                      </span>
                    )}
                  </div>
                  {hasBlocks && <div className="mt-0.5 text-xs text-muted-foreground">Voorkeur: {voorkeur}</div>}
                  {addon !== "Geen add-ons" && <div className="mt-0.5 text-xs text-muted-foreground">{addon}</div>}
                </div>
              );
            })}
          </div>
        )}
        {s.notes && <p className="mt-1.5 text-xs italic text-muted-foreground">“{s.notes}”</p>}
      </div>
    );
  }

  const vast = byMode("vast");
  const keuze = byMode("keuze");
  const technohub = byMode("technohub");
  const deskundigheidsbevordering = byMode("deskundigheidsbevordering");
  const maatwerk = byMode("maatwerk");
  const keuzeOut = optedOutGroups("keuze");
  const technohubOut = optedOutGroups("technohub");
  const technohubSectionOut = sectionOptOuts.includes("technohub");
  const deskOut = sectionOptOuts.includes("deskundigheidsbevordering");
  const maatwerkOut = sectionOptOuts.includes("maatwerk");

  return (
    <div className="space-y-5">
      <ProgCategory title="Vast- & keuzeprogramma">
        {vast.length === 0 && keuze.length === 0 && keuzeOut.length === 0 ? (
          <ProgEmpty text="Geen vast- of keuzeprogramma gekozen." />
        ) : (
          <div className="divide-y divide-border rounded-lg border">
            {vast.map(renderSelection)}
            {keuze.map(renderSelection)}
            {keuzeOut.length > 0 && <ProgOptOut label="Doet niet mee aan het keuzeprogramma" groups={keuzeOut} />}
          </div>
        )}
      </ProgCategory>

      <ProgCategory title="TechnoHub">
        {technohub.length === 0 && technohubOut.length === 0 && !technohubSectionOut ? (
          <ProgEmpty text="Geen TechnoHub-workshops gekozen." />
        ) : (
          <div className="divide-y divide-border rounded-lg border">
            {technohub.map(renderSelection)}
            {technohubSectionOut && <ProgSectionOptOut text="Doet dit schooljaar niet mee aan TechnoHub." />}
            {technohubOut.length > 0 && <ProgOptOut label="Doet niet mee aan TechnoHub" groups={technohubOut} />}
          </div>
        )}
      </ProgCategory>

      <ProgCategory title="Deskundigheidsbevordering">
        {deskOut ? (
          <ProgSectionOptOut text="Neemt dit schooljaar geen deskundigheidsbevordering af." />
        ) : deskundigheidsbevordering.length === 0 ? (
          <ProgEmpty text="Geen training gekozen." />
        ) : (
          <div className="divide-y divide-border rounded-lg border">{deskundigheidsbevordering.map(renderSelection)}</div>
        )}
      </ProgCategory>

      <ProgCategory title="Extra maatwerk">
        {maatwerkOut ? (
          <ProgSectionOptOut text="Neemt dit schooljaar geen extra maatwerk af." />
        ) : maatwerk.length === 0 ? (
          <ProgEmpty text="Geen maatwerk aangevraagd." />
        ) : (
          <div className="divide-y divide-border rounded-lg border">{maatwerk.map(renderSelection)}</div>
        )}
      </ProgCategory>
    </div>
  );
}

// Mirror of ProgrammaReviewSection's category heading + body, opt-out lines.
function ProgCategory({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}
function ProgEmpty({ text }: { text: string }) {
  return <p className="text-sm text-muted-foreground">{text}</p>;
}
function ProgOptOut({ label, groups }: { label: string; groups: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-4 py-3">
      <XCircleIcon size={13} className="shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">{label}:</span>
      {groups.map((g) => (
        <Badge key={g} variant="outline" size="sm">{g}</Badge>
      ))}
    </div>
  );
}
function ProgSectionOptOut({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3">
      <XCircleIcon size={14} className="shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">{text}</span>
    </div>
  );
}

// ═══════════════════════════ PER-FLOW SECTION ═══════════════════════════
// One self-contained section for a single flow ("schoolgegevens" or "programmaselectie"). It owns its
// own selected-version state, its own diff toggle, and renders the matching view. The snapshot fetch +
// cache is passed in from the parent so both sections share one cache. `versions` here is ONLY this
// flow's versions, newest first.

type FlowKey = "schoolgegevens" | "programmaselectie";
const FLOW_NOUN: Record<FlowKey, string> = {
  schoolgegevens: "schoolgegevens",
  programmaselectie: "programmaselectie",
};

function FlowSection({
  flow,
  versions,
  fetchVersion,
}: {
  flow: FlowKey;
  versions: VersionMeta[];
  fetchVersion: (n: number) => Promise<Submission | null>;
}) {
  const versionSelectId = useId();
  const latestNo = versions[0]?.versionNo ?? null;
  const [viewNo, setViewNo] = useState<number | null>(latestNo);

  // Display numbering is flow-relative: the oldest version of THIS flow is "v1", etc. The internal
  // version_no runs per-school across BOTH flows (so e.g. a school's first programmaselectie can be
  // global v2 because schoolgegevens was v1), which is confusing in a per-flow selector. We keep the
  // global version_no as the value used to fetch a snapshot, but only ever SHOW the flow-relative one.
  // `versions` is newest-first, so the oldest is at the end.
  const displayNoOf = (versionNo: number) => {
    const idx = versions.findIndex((v) => v.versionNo === versionNo);
    return idx === -1 ? versionNo : versions.length - idx;
  };
  const [viewData, setViewData] = useState<Submission | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [prevData, setPrevData] = useState<Submission | null>(null);

  // The previous version of this flow (the one directly older than the selected one).
  const prevNo = useMemo(() => {
    if (viewNo == null) return null;
    return versions.find((v) => v.versionNo < viewNo)?.versionNo ?? null;
  }, [versions, viewNo]);

  // Load the selected version's snapshot.
  useEffect(() => {
    if (viewNo == null) return;
    let cancelled = false;
    setViewData(null);
    fetchVersion(viewNo).then((v) => { if (!cancelled && v) setViewData(v); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewNo]);

  // Load the previous version's snapshot for the diff (only when the diff is shown).
  useEffect(() => {
    if (!showDiff || prevNo == null) { setPrevData(null); return; }
    let cancelled = false;
    fetchVersion(prevNo).then((v) => { if (!cancelled) setPrevData(v); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDiff, prevNo]);

  const flowTitle = FLOW_LABEL[flow];

  // School never submitted this flow.
  if (versions.length === 0) {
    return (
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">{flowTitle}</h2>
        <div className="rounded-lg border bg-muted/20 px-6 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            Deze school heeft nog geen {FLOW_NOUN[flow]} aangeleverd.
          </p>
        </div>
      </section>
    );
  }

  const data = viewData;
  const viewingOld = viewNo != null && latestNo != null && viewNo !== latestNo;
  const diffDisabled = prevNo == null;

  return (
    <section className="space-y-4">
      <h2 className="text-base font-semibold text-foreground">{flowTitle}</h2>

      {/* Per-section version toolbar: this flow's versions only, latest by default, with a diff
          toggle against the previous version of THIS flow. */}
      <div className="rounded-lg border bg-muted/20 p-4">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={versionSelectId} className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <HistoryIcon size={13} /> Versie
            </label>
            <Select
              items={Object.fromEntries(versions.map((v) => [String(v.versionNo), `v${displayNoOf(v.versionNo)} · ${fmtDateTime(v.submittedAt)}`]))}
              value={viewNo != null ? String(viewNo) : null}
              onValueChange={(v) => setViewNo(Number(v))}
            >
              <SelectTrigger id={versionSelectId} size="sm" aria-label="Versie" className="w-full sm:w-80"><SelectValue /></SelectTrigger>
              <SelectPopup>
                {versions.map((v) => (
                  <SelectItem key={v.versionNo} value={String(v.versionNo)}>
                    v{displayNoOf(v.versionNo)} · {fmtDateTime(v.submittedAt)}{v.versionNo === latestNo ? " · nieuwste" : ""}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </div>

          {data && (
            <Button variant="outline" size="sm" onClick={() => downloadCsv(data, buildCsv(data))}>
              <DownloadIcon size={15} /> Export CSV
            </Button>
          )}
        </div>

        {/* Metadata for the viewed version, on one tidy line. */}
        {data && (
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t pt-3 text-xs text-muted-foreground">
            {data.submittedAt && (<span>Aangeleverd op <span className="text-foreground">{fmtDateTime(data.submittedAt)}</span></span>)}
            {data.submittedBy && (<><span aria-hidden="true">·</span><span className="max-w-[16rem] truncate" title={data.submittedBy}>door {data.submittedBy}</span></>)}
            {data.postDeadline && (<Badge variant="warning" size="sm" className="ms-0.5 gap-1"><AlertTriangleIcon size={12} /> Na de deadline</Badge>)}
          </div>
        )}

        {/* Diff toggle: compare to the previous version of THIS flow. Off by default. */}
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t pt-3">
          <label className={`flex items-center gap-2 text-sm ${diffDisabled ? "cursor-not-allowed text-muted-foreground" : "cursor-pointer text-foreground"}`}>
            <Checkbox
              checked={showDiff && !diffDisabled}
              disabled={diffDisabled}
              onCheckedChange={(v) => setShowDiff(Boolean(v))}
            />
            Toon wijzigingen ten opzichte van vorige versie
          </label>
          {diffDisabled && (
            <span className="text-xs text-muted-foreground">Er is geen eerdere {FLOW_NOUN[flow]} om mee te vergelijken.</span>
          )}
        </div>

        {viewingOld && (
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-700">
            <AlertTriangleIcon size={13} className="shrink-0" /> U bekijkt een oudere versie (v{displayNoOf(viewNo)} van v{latestNo != null ? displayNoOf(latestNo) : "?"}).
            <button type="button" onClick={() => latestNo != null && setViewNo(latestNo)} className="cursor-pointer font-medium text-primary hover:underline">Nieuwste tonen</button>
          </p>
        )}
      </div>

      {showDiff && !diffDisabled && prevData && viewNo != null && prevNo != null && (
        <VersionDiff base={prevData} target={data!} baseNo={displayNoOf(prevNo)} targetNo={displayNoOf(viewNo)} flow={flow} />
      )}

      {data ? (
        flow === "programmaselectie" ? <ProgrammaselectieView data={data} /> : <SchoolgegevensView data={data} />
      ) : (
        <p className="text-muted-foreground">Versie laden...</p>
      )}
    </section>
  );
}

// ═══════════════════════════ MAIN VIEW ═══════════════════════════

// Read-only view of one school's submitted data + CSV export. Rendered inside the school dossier
// (/admin/schools/:id "Aanlevering" tab). Two always-visible sections, one per flow, each with its
// OWN version selector and diff toggle, so the director can check everything a school submitted.
// KUVO sees only submitted versions, never concepts. The old /admin/submissions/:id route redirects
// to the dossier (AdminSubmissionPage).
export function SchoolAanleveringView({ schoolId, onSubmissionState }: { schoolId: number; onSubmissionState?: (hasSubmission: boolean) => void }) {
  const [versions, setVersions] = useState<VersionMeta[]>([]);
  const [hasSubmission, setHasSubmission] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const cacheRef = useRef<Record<number, Submission>>({});

  const schoolsApi = api.api.admin.schools as any;
  const fetchVersion = async (n: number): Promise<Submission | null> => {
    if (cacheRef.current[n]) return cacheRef.current[n];
    try {
      const res = await schoolsApi[":id"]["submission-versions"][":versionNo"].$get({
        param: { id: String(schoolId), versionNo: String(n) },
      });
      if (!res.ok) return null;
      const v = (await res.json()) as Submission;
      cacheRef.current[n] = v;
      return v;
    } catch { return null; }
  };

  // Initial load: the version timeline (newest first), across both flows.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await schoolsApi[":id"]["submission-versions"].$get({ param: { id: String(schoolId) } });
        if (!res.ok) { if (!cancelled) { setError(true); setLoading(false); } return; }
        const d = await res.json();
        const list: VersionMeta[] = d.versions ?? [];
        if (cancelled) return;
        setVersions(list);
        setHasSubmission(list.length > 0);
        onSubmissionState?.(list.length > 0);
      } catch { if (!cancelled) setError(true); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  // Split the timeline into per-flow lists (each already newest-first since the source is).
  const schoolgegevensVersions = useMemo(
    () => versions.filter((v) => v.flow === "schoolgegevens"),
    [versions],
  );
  const programmaselectieVersions = useMemo(
    () => versions.filter((v) => v.flow === "programmaselectie"),
    [versions],
  );

  if (loading) return <p className="text-muted-foreground">Aanlevering laden...</p>;
  if (error) return <p className="text-sm text-destructive">Kan de aanlevering niet laden.</p>;
  if (hasSubmission === false) {
    return (
      <div className="rounded-lg border bg-muted/20 px-6 py-10 text-center">
        <p className="text-sm font-medium text-foreground">Nog niet ingediend</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Deze school heeft nog geen aanlevering ingediend. Zodra de school indient, ziet u hier de
          meest recente versie. U ziet alleen ingediende versies, geen concepten.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <FlowSection flow="schoolgegevens" versions={schoolgegevensVersions} fetchVersion={fetchVersion} />
      <FlowSection flow="programmaselectie" versions={programmaselectieVersions} fetchVersion={fetchVersion} />
    </div>
  );
}

// Old standalone route → the school dossier (which hosts the Aanlevering view).
export function AdminSubmissionPage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  return <Navigate to={`/admin/schools/${schoolId}`} replace />;
}
